// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/tag.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / actions / custom-tabs).

import type { DetailPageConfig } from "../../components/composition/DetailPage";
import { tagsApi } from "../../api/tags";
import type { Tag } from "../../api/types";
// override-block:imports BEGIN
import { createElement, useEffect, useState } from "react";
import type { TagApplication, TagApplicationListResponse } from "../../api/types";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// override-block:imports END

// override-block:helpers BEGIN
// CHG-1377 — dashSpan + colorChip helpers from the pre-migration hand-roll
// were carried as dead code (no internal usage; only re-exported, with no
// consumers). Dropped during migration. tag-list's `colorSwatch` is the
// canonical color rendering helper for tag UI; if a future caller needs
// the same on a detail surface, lift it to a shared component instead of
// re-declaring it here.

// ApplicationList — renders a tab's per-entity-type application list.
// CHG-1334 / DRIFT-649 closeout: backed by the real inverse-lookup
// endpoint via tagsApi.listTagXxx (which call listTagApplications
// with the matching entity_type). Each row renders the server-resolved
// entity_label + optional entity_subtitle.
function ApplicationList({
  tagId,
  fetcher,
  emptyLabel,
}: {
  tagId: string;
  fetcher: (id: string) => Promise<TagApplicationListResponse>;
  emptyLabel: string;
}) {
  const [state, setState] = useState<{
    loading: boolean;
    items: TagApplication[];
    total: number;
  }>({ loading: true, items: [], total: 0 });

  useEffect(() => {
    let cancelled = false;
    fetcher(tagId).then(
      (r) => {
        if (cancelled) return;
        setState({ loading: false, items: r.items, total: r.total_count });
      },
      () => {
        if (cancelled) return;
        setState({ loading: false, items: [], total: 0 });
      },
    );
    return () => { cancelled = true; };
  }, [tagId, fetcher]);

  if (state.loading) {
    return createElement(
      "div",
      { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
      "Loading…",
    );
  }
  if (state.items.length === 0) {
    return createElement(
      "div",
      { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
      emptyLabel,
    );
  }
  return createElement(
    "div",
    { style: { display: "flex", flexDirection: "column", gap: 6 } },
    state.items.map((app) =>
      createElement(
        "div",
        {
          key: `${app.entity_type}:${app.entity_id}`,
          className: "card",
          style: { padding: 12, fontSize: 13 },
        },
        createElement(
          "div",
          { style: { fontWeight: 500 } },
          app.entity_label,
        ),
        app.entity_subtitle
          ? createElement(
              "div",
              { style: { color: "var(--color-text-muted)", fontSize: 12 } },
              app.entity_subtitle,
            )
          : null,
      ),
    ),
  );
}
// override-block:helpers END

export const tagDetailConfig: DetailPageConfig<Tag> = {
  idParam: "tagId",
  queryKey: (id: string) => ["tags", "detail", id] as const,
  // override-block:query-fn BEGIN
  // CHG-1334 — getTag's safeFetch converts 404 to null; render a placeholder
  // Tag with zeroed counts so DetailPage's title/subtitle/cards don't blow up.
  queryFn: async (id: string) => {
    const t = await tagsApi.getTag(id);
    if (!t) {
      const now = new Date(0).toISOString();
      return {
        id,
        organization_id: "",
        name: "(Tag not found)",
        color: "#cccccc",
        created_at: now,
        updated_at: now,
        applied_to_count: 0,
        applied_to_donor_count: 0,
        applied_to_campaign_count: 0,
        applied_to_recurring_donation_count: 0,
        applied_to_pledge_count: 0,
        applied_to_donation_count: 0,
        applied_to_message_count: 0,
      } satisfies Tag;
    }
    return t;
  },
  // override-block:query-fn END
  backTo: "/tags",
  // override-block:title BEGIN
  title: (r) => r.name,
  // override-block:title END
  // override-block:subtitle BEGIN
  subtitle: (r) => r.description ?? null,
  // override-block:subtitle END
  statusCards: (r, _fmt) => [
    // override-block:statcard-donors BEGIN
    {
      id: "donors",
      label: "Donors",
      value: r.applied_to_donor_count ?? 0,
      format: "number",
      tone: "g-mygiving",
    },
    // override-block:statcard-donors END
    // override-block:statcard-campaigns BEGIN
    {
      id: "campaigns",
      label: "Campaigns",
      value: r.applied_to_campaign_count ?? 0,
      format: "number",
      tone: "g-giving",
    },
    // override-block:statcard-campaigns END
    // override-block:statcard-recurring BEGIN
    {
      id: "recurring",
      label: "Recurring",
      value: r.applied_to_recurring_donation_count ?? 0,
      format: "number",
      tone: "g-automation",
    },
    // override-block:statcard-recurring END
    // override-block:statcard-pledges BEGIN
    {
      id: "pledges",
      label: "Pledges",
      value: r.applied_to_pledge_count ?? 0,
      format: "number",
      tone: "g-home",
    },
    // override-block:statcard-pledges END
  ],
  actions: (_r) => [
    // override-block:actions BEGIN
    { id: "edit", label: "Edit", variant: "secondary", permissionGate: toGate(GATE_CATALOG.tag_detail["edit"], "disable") },
    { id: "merge", label: "Merge", variant: "primary", permissionGate: toGate(GATE_CATALOG.tag_detail["merge"], "disable") },
    { id: "delete", label: "Delete", variant: "ghost", permissionGate: toGate(GATE_CATALOG.tag_detail["delete"], "disable") },
    // override-block:actions END
  ],
  tabs: [
    // override-block:custom-tabs BEGIN
    // 6 tabs — one per canonical CHG-1311 entity_type. CHG-1334 closed
    // DRIFT-649 by shipping the inverse-lookup endpoint; donations +
    // messages tabs were added at the same time since their data is now
    // reachable. Each tab fetches via tagsApi.listTagXxx which dispatches
    // to listTagApplications(tag_id, entity_type=<type>).
    {
      id: "donors",
      label: "Donors",
      render: (t) =>
        createElement(ApplicationList, {
          tagId: t.id,
          fetcher: tagsApi.listTagDonors,
          emptyLabel: "No donors tagged yet.",
        }),
    },
    {
      id: "campaigns",
      label: "Campaigns",
      render: (t) =>
        createElement(ApplicationList, {
          tagId: t.id,
          fetcher: tagsApi.listTagCampaigns,
          emptyLabel: "No campaigns tagged yet.",
        }),
    },
    {
      id: "recurring",
      label: "Recurring",
      render: (t) =>
        createElement(ApplicationList, {
          tagId: t.id,
          fetcher: tagsApi.listTagRecurring,
          emptyLabel: "No recurring donations tagged yet.",
        }),
    },
    {
      id: "pledges",
      label: "Pledges",
      render: (t) =>
        createElement(ApplicationList, {
          tagId: t.id,
          fetcher: tagsApi.listTagPledges,
          emptyLabel: "No pledges tagged yet.",
        }),
    },
    {
      id: "donations",
      label: "Donations",
      render: (t) =>
        createElement(ApplicationList, {
          tagId: t.id,
          fetcher: tagsApi.listTagDonations,
          emptyLabel: "No donations tagged yet.",
        }),
    },
    {
      id: "messages",
      label: "Messages",
      render: (t) =>
        createElement(ApplicationList, {
          tagId: t.id,
          fetcher: tagsApi.listTagMessages,
          emptyLabel: "No messages tagged yet.",
        }),
    },
    // override-block:custom-tabs END
  ],
};
